import { useSuspenseQuery } from "@apollo/client";
import { Modal } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import Link from "next/link";

import {
  ArrowForwardIcon,
  Button,
  Center,
  CheckIcon,
  Divider,
  Group,
  Paper,
  Rating,
  Stack,
  Text,
} from "@/components/ui/elements";

import { workerIdQuery } from "./_graphql";

type Props = {
  opened: boolean;
  close: () => void;
  average: number;
  hasNextReview?: boolean;
  onNextReview?: () => void;
};

export function SendReviewModal({
  opened,
  close,
  average,
  hasNextReview = false,
  onNextReview,
}: Props) {
  const {
    data: { viewer },
  } = useSuspenseQuery(workerIdQuery);
  const isMobile = useMediaQuery("(max-width: 768px)");

  const handleNextReview = () => {
    close();
    onNextReview?.();
  };

  return (
    <Modal
      opened={opened}
      onClose={close}
      centered
      zIndex={10000}
      size={isMobile ? "md" : "lg"}
    >
      <Stack align="center" gap="lg" p={isMobile ? "md" : "xl"}>
        {/* 成功アイコン */}
        <div
          style={{
            width: isMobile ? "70px" : "80px",
            height: isMobile ? "70px" : "80px",
            borderRadius: "50%",
            backgroundColor: "#f0fdf4",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "3px solid #22c55e",
          }}
        >
          <CheckIcon size={isMobile ? 35 : 40} color="#22c55e" />
        </div>

        {/* メインメッセージ */}
        <Stack align="center" gap="xs">
          <Text size={isMobile ? "lg" : "xl"} fw="bold" c="green" ta="center">
            レビューを送信しました！
          </Text>
          <Text size={isMobile ? "sm" : "md"} c="dimmed" ta="center">
            ご協力ありがとうございました
          </Text>
        </Stack>

        {/* 評価表示 */}
        <Stack align="center" gap="xs" p="md" style={{ width: "100%" }}>
          <Text fz={isMobile ? "sm" : "md"} c="dimmed" ta="center">
            総合評価
          </Text>
          <Group gap="md" align="center" justify="center">
            <Text fz={isMobile ? "lg" : "xl"} fw="bold" c="teal">
              {average.toFixed(2)}
            </Text>
            <Rating
              size={isMobile ? "lg" : "xl"}
              value={average}
              readOnly
              fractions={4}
            />
          </Group>
        </Stack>

        {/* レビューお礼と次の勤務への誘導メッセージ */}
        <Paper
          p={isMobile ? "md" : "lg"}
          radius="md"
          style={{
            width: "100%",
            background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
            border: "2px solid #bae6fd",
          }}
        >
          <Stack align="center" gap="sm">
            <Text fz={isMobile ? "lg" : "xl"} fw="bold" c="blue.7" ta="center">
              🎉 レビューありがとうございます！
            </Text>
            <Text
              fz={isMobile ? "xs" : "sm"}
              c="gray.7"
              ta="center"
              style={{ lineHeight: 1.7 }}
            >
              あなたの貴重なご意見が、
              <br />
              より良い職場環境づくりに役立ちます。
            </Text>
            <Divider w="60%" my="xs" />
            {hasNextReview ? (
              <>
                <Text
                  fz={isMobile ? "sm" : "md"}
                  fw={600}
                  c="teal.6"
                  ta="center"
                >
                  📝 続けてレビューを入力しましょう！
                </Text>
                <Text
                  fz={isMobile ? "xs" : "sm"}
                  c="dimmed"
                  ta="center"
                  style={{ lineHeight: 1.6 }}
                >
                  他の勤務のレビューも
                  <br />
                  お待ちしています！
                </Text>
              </>
            ) : (
              <>
                <Text
                  fz={isMobile ? "sm" : "md"}
                  fw={600}
                  c="blue.6"
                  ta="center"
                >
                  💼 次のお仕事を探してみませんか？
                </Text>
                <Text
                  fz={isMobile ? "xs" : "sm"}
                  c="dimmed"
                  ta="center"
                  style={{ lineHeight: 1.6 }}
                >
                  あなたにぴったりの求人が
                  <br />
                  きっと見つかります！
                </Text>
              </>
            )}
          </Stack>
        </Paper>

        {/* ボタン */}
        <Stack gap="sm" style={{ width: "100%" }} mt="md">
          {hasNextReview && onNextReview && (
            <>
              <Button
                onClick={handleNextReview}
                size={isMobile ? "md" : "lg"}
                fullWidth
                color="teal"
                rightSection={<ArrowForwardIcon size={20} />}
                style={{
                  fontWeight: "bold",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                }}
              >
                次のレビューを入力する
              </Button>
              <Group grow gap="sm" mt="xs">
                <Button
                  variant="light"
                  onClick={close}
                  size={isMobile ? "sm" : "md"}
                  fullWidth
                  color="gray"
                >
                  閉じる
                </Button>
                <Button
                  component={Link}
                  href={"/work"}
                  size={isMobile ? "sm" : "md"}
                  fullWidth
                  variant="outline"
                  color="blue"
                  rightSection={<ArrowForwardIcon size={16} />}
                >
                  求人一覧へ
                </Button>
              </Group>
            </>
          )}
          {!hasNextReview && (
            <Group grow gap="sm">
              <Button
                variant="light"
                onClick={close}
                size={isMobile ? "sm" : "md"}
                fullWidth
                color="gray"
              >
                閉じる
              </Button>
              <Button
                component={Link}
                href={"/work"}
                size={isMobile ? "sm" : "md"}
                fullWidth
                color="blue"
                rightSection={<ArrowForwardIcon size={16} />}
              >
                新しいお仕事を探す
              </Button>
            </Group>
          )}
        </Stack>
      </Stack>
    </Modal>
  );
}
